import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ChannelsArrowIcon,
  ChannelsEmptyBlock,
  ChannelsEmptyContainer,
  ChannelsListContainer,
  ChannelsMainContainer,
  ChannelsMainLoader,
  ChannelsPlaceholderTextMessage,
  ChannelsPlaceholderTextTitle,
  ChannelsTitleBlock,
  LoaderContainer
} from '@/components/ChannelsList/styled';
import { ErrorMessage } from '@/components/ErrorMessage';
import { InfiniteScroll } from '@/components/InfiniteScroll';
import { Loader } from '@/components/ui/Loader';
import { useChatActions } from '@/hooks/useChatActions';
import useDeviceType from '@/hooks/useDeviceType';
import { useAuth } from '@/providers/AuthProvider';
import { useChat } from '@/providers/ChatProvider';
import { sortChannels } from '@/utils/helpers/sortChannels';
import { useScrollRestoration } from '@/utils/hooks/useScrollRestoration';

import { ChannelItem } from './ChannelItem';

interface ChannelsListProps {
  selectedChannelId?: string;
}

export const ChannelsList: React.FC<ChannelsListProps> = ({
  selectedChannelId
}) => {
  const { t } = useTranslation();
  const { user, isAuthReady } = useAuth();
  const { isMobile } = useDeviceType();
  const { getUserChats } = useChatActions();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const { channels, chatError, isChatsLoaded } = useChat();
  const [nextToken, setNextToken] = useState<string | null>(null);
  const sortedChannels = useMemo(() => sortChannels(channels), [channels]);

  const loadingComponent = (
    <LoaderContainer>
      <Loader />
    </LoaderContainer>
  );

  const fetchChannels = useCallback(
    async (loadMore = false) => {
      if (loading) return;

      setLoading(true);

      try {
        const resp = await getUserChats({ nextToken, loadMore });
        setNextToken(resp?.nextToken ?? null);
        setHasMore(Boolean(resp?.nextToken));
      } catch (e) {
        console.error('Error fetching channels:', e);
      } finally {
        setLoading(false);
      }
    },
    [getUserChats, nextToken, loading]
  );

  const loadMoreChannels = useCallback(async () => {
    if (!hasMore || loading) return;

    await fetchChannels(true);
  }, [hasMore, loading, fetchChannels]);

  useScrollRestoration({
    selector: '.channels-scroll',
    storageKey: 'channelsScrollPosition'
  });

  useEffect(() => {
    if (user?.id && isAuthReady && !isChatsLoaded) {
      fetchChannels(false);
    }
  }, [user?.id, isAuthReady, isChatsLoaded]);

  if (chatError) {
    return (
      <ChannelsMainContainer className="channels">
        <ChannelsTitleBlock>{t('messages')}</ChannelsTitleBlock>
        {isMobile ? <ErrorMessage /> : null}
      </ChannelsMainContainer>
    );
  }

  if (!isChatsLoaded) {
    return (
      <ChannelsMainContainer className="channels">
        <ChannelsTitleBlock>{t('messages')}</ChannelsTitleBlock>
        <ChannelsMainLoader>
          <Loader />
        </ChannelsMainLoader>
      </ChannelsMainContainer>
    );
  }

  return (
    <ChannelsMainContainer className="channels">
      <ChannelsTitleBlock>{t('messages')}</ChannelsTitleBlock>
      {sortedChannels?.length ? (
        <ChannelsListContainer>
          <InfiniteScroll
            hasMore={hasMore}
            initialLoad={false}
            onInfiniteLoad={loadMoreChannels}
            isInfiniteLoading={loading}
            useWindow={false}
            loadingComponent={loadingComponent}
            threshold={50}
            className="channels-scroll"
          >
            {sortedChannels.map(channel => (
              <ChannelItem
                key={channel.id}
                channel={channel}
                isSelected={channel.id === selectedChannelId}
              />
            ))}
          </InfiniteScroll>
        </ChannelsListContainer>
      ) : (
        <ChannelsEmptyContainer>
          <ChannelsEmptyBlock>
            <ChannelsArrowIcon />
            <ChannelsPlaceholderTextTitle>
              {t('empty.title')}
            </ChannelsPlaceholderTextTitle>
            <ChannelsPlaceholderTextMessage>
              {t('empty.message')}
            </ChannelsPlaceholderTextMessage>
          </ChannelsEmptyBlock>
        </ChannelsEmptyContainer>
      )}
    </ChannelsMainContainer>
  );
};
